import { useState } from 'react';
import type { Movie } from '@/types/movie';

export default function DetailTabs({ movie }: { movie: Movie }) {
  const tabs = [
    { key: 'story', label: '剧情简介' },
    ...(movie.episodes ? [{ key: 'episodes', label: '剧集列表' }] : []),
    { key: 'guide', label: '观影指南' },
  ];
  const [active, setActive] = useState('story');
  const [selectedEp, setSelectedEp] = useState(1);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
      <div className="flex items-center gap-1 p-1.5 border-b border-background-200 bg-background-100 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            className={`h-9 px-4 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${active === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-5 md:p-6">
        {active === 'story' && (
          <div className="max-w-3xl">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{movie.summary}</p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              本片由 <span className="font-medium text-foreground-900">{movie.director}</span> 执导，
              {' '}{movie.actors.slice(0, 3).join('、')} 等主演，是一部融合 {movie.genres.join('、')} 元素的{movie.region}作品。
              影片以细腻的叙事节奏展开，在保持故事张力的同时，也留下了足够回味的情感空间。
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: 'ri-lightbulb-line', label: '看点', text: '叙事结构新颖，人物弧光完整，情绪层层递进。' },
                { icon: 'ri-award-line', label: '亮点', text: `站内评分 ${movie.rating.toFixed(1)}，属于本站口碑前列作品。` },
              ].map((c) => (
                <div key={c.label} className="flex gap-3 p-4 rounded-md bg-background-100">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{c.label}</p>
                    <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {active === 'episodes' && movie.episodes && (
          <div>
            <p className="text-sm text-foreground-500 mb-4">共 {movie.episodes} 集 · 当前选中 第 {selectedEp} 集（演示数据，不提供播放）</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 max-h-72 overflow-y-auto pr-1">
              {Array.from({ length: movie.episodes }).map((_, i) => {
                const ep = i + 1;
                return (
                  <button
                    key={ep}
                    type="button"
                    onClick={() => setSelectedEp(ep)}
                    className={`h-10 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${selectedEp === ep ? 'bg-accent-500 text-accent-950 font-semibold' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                  >
                    {ep}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {active === 'guide' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: 'ri-time-line', title: '适合什么时候看', text: movie.type === 'variety' ? '饭点或睡前放一集，轻松不费脑，非常适合下饭。' : '建议留出一整段完整时间观看，剧情连贯、情绪不宜被打断。' },
              { icon: 'ri-group-line', title: '适合和谁一起', text: movie.genres.includes('爱情') ? '适合和另一半一起，看完还能聊很久。' : '适合和家人朋友一起，尤其是喜欢' + movie.genres[0] + '题材的人。' },
              { icon: 'ri-emotion-happy-line', title: '观看前的小提示', text: movie.genres.includes('悬疑') || movie.genres.includes('犯罪') ? '留意前期的细节铺垫，很多伏笔会在后半段揭晓。' : '放松心情即可，这是一部节奏舒服、适合慢慢品味的作品。' },
            ].map((g) => (
              <div key={g.title} className="p-4 rounded-md bg-background-100">
                <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className={`${g.icon} text-lg`} />
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground-900">{g.title}</p>
                <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{g.text}</p>
              </div>
            ))}
            <p className="md:col-span-3 text-xs text-foreground-400">观影指南为编辑推荐内容，久久热不提供在线播放与下载服务。</p>
          </div>
        )}
      </div>
    </div>
  );
}